// src/rentals-api-v3/models/CurrencyValue.ts
function CurrencyValueFromJSON(json) {
  return CurrencyValueFromJSONTyped(json, false);
}
function CurrencyValueFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    currencyCode: json["currencyCode"],
    value: json["value"]
  };
}

// src/rentals-api-v3/runtime.ts
import { formatISO } from "date-fns";
var BASE_PATH = "https://rentals.api.jucy.cloud".replace(/\/+$/, "");
var Configuration = class {
  constructor(configuration = {}) {
    this.configuration = configuration;
  }
  set config(configuration) {
    this.configuration = configuration;
  }
  get basePath() {
    return this.configuration.basePath != null ? this.configuration.basePath : BASE_PATH;
  }
  get fetchApi() {
    return this.configuration.fetchApi || fetch;
  }
  get middleware() {
    return this.configuration.middleware || [];
  }
  get queryParamsStringify() {
    return this.configuration.queryParamsStringify || querystring;
  }
  get username() {
    return this.configuration.username;
  }
  get password() {
    return this.configuration.password;
  }
  get apiKey() {
    const apiKey = this.configuration.apiKey;
    if (apiKey) {
      return typeof apiKey === "function" ? apiKey : () => apiKey;
    }
    return void 0;
  }
  get accessToken() {
    const accessToken = this.configuration.accessToken;
    if (accessToken) {
      return typeof accessToken === "function" ? accessToken : async () => accessToken;
    }
    return void 0;
  }
  get headers() {
    return this.configuration.headers;
  }
  get credentials() {
    return this.configuration.credentials;
  }
};
var DefaultConfig = new Configuration();
async function createResponseErrorMessage(response, fallback = "Response returned an error code") {
  try {
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.startsWith("application/json")) {
      const json = await response.json();
      return new ResponseError(response, json?.message || json?.Message || response.statusText || fallback, json);
    }
    const body = await response.text();
    return new ResponseError(response, body || response.statusText || fallback, { body });
  } catch (e) {
    return new ResponseError(response, fallback, { error: e });
  }
}
var BaseAPI = class _BaseAPI {
  constructor(configuration = DefaultConfig) {
    this.configuration = configuration;
    this.middleware = configuration.middleware;
  }
  static jsonRegex = new RegExp("^(:?application/json|[^;/ 	]+/[^;/ 	]+[+]json)[ 	]*(:?;.*)?$", "i");
  middleware;
  withMiddleware(...middlewares) {
    const next = this.clone();
    next.middleware = next.middleware.concat(...middlewares);
    return next;
  }
  withPreMiddleware(...preMiddlewares) {
    const middlewares = preMiddlewares.map((pre) => ({ pre }));
    return this.withMiddleware(...middlewares);
  }
  withPostMiddleware(...postMiddlewares) {
    const middlewares = postMiddlewares.map((post) => ({ post }));
    return this.withMiddleware(...middlewares);
  }
  /**
   * Check if the given MIME is a JSON MIME.
   * JSON MIME examples:
   *   application/json
   *   application/json; charset=UTF8
   *   APPLICATION/JSON
   *   application/vnd.company+json
   * @param mime - MIME (Multipurpose Internet Mail Extensions)
   * @return True if the given MIME is JSON, false otherwise.
   */
  isJsonMime(mime) {
    if (!mime) {
      return false;
    }
    return _BaseAPI.jsonRegex.test(mime);
  }
  async request(context, initOverrides) {
    const { url, init } = await this.createFetchParams(context, initOverrides);
    const response = await this.fetchApi(url, init);
    if (response && response.status >= 200 && response.status < 300) {
      return response;
    }
    throw await createResponseErrorMessage(response, "Response returned an error code");
  }
  async createFetchParams(context, initOverrides) {
    let url = this.configuration.basePath + context.path;
    if (context.query !== void 0 && Object.keys(context.query).length !== 0) {
      url += "?" + this.configuration.queryParamsStringify(context.query);
    }
    const headers = Object.assign({}, this.configuration.headers, context.headers);
    Object.keys(headers).forEach((key) => headers[key] === void 0 ? delete headers[key] : {});
    const initOverrideFn = typeof initOverrides === "function" ? initOverrides : async () => initOverrides;
    const initParams = {
      method: context.method,
      headers,
      body: context.body,
      credentials: this.configuration.credentials
    };
    const overriddenInit = {
      ...initParams,
      ...await initOverrideFn({
        init: initParams,
        context
      })
    };
    let body;
    if (isFormData(overriddenInit.body) || overriddenInit.body instanceof URLSearchParams || isBlob(overriddenInit.body)) {
      body = overriddenInit.body;
    } else if (this.isJsonMime(headers["Content-Type"])) {
      body = JSON.stringify(overriddenInit.body);
    } else {
      body = overriddenInit.body;
    }
    const init = {
      ...overriddenInit,
      body
    };
    return { url, init };
  }
  fetchApi = async (url, init) => {
    let fetchParams = { url, init };
    for (const middleware of this.middleware) {
      if (middleware.pre) {
        fetchParams = await middleware.pre({
          fetch: this.fetchApi,
          ...fetchParams
        }) || fetchParams;
      }
    }
    let response = void 0;
    try {
      response = await (this.configuration.fetchApi || fetch)(fetchParams.url, fetchParams.init);
    } catch (e) {
      for (const middleware of this.middleware) {
        if (middleware.onError) {
          response = await middleware.onError({
            fetch: this.fetchApi,
            url: fetchParams.url,
            init: fetchParams.init,
            error: e,
            response: response ? response.clone() : void 0
          }) || response;
        }
      }
      if (response === void 0) {
        if (e instanceof Error) {
          throw new FetchError(e, "The request failed and the interceptors did not return an alternative response");
        } else {
          throw e;
        }
      }
    }
    for (const middleware of this.middleware) {
      if (middleware.post) {
        response = await middleware.post({
          fetch: this.fetchApi,
          url: fetchParams.url,
          init: fetchParams.init,
          response: response.clone()
        }) || response;
      }
    }
    return response;
  };
  /**
   * Create a shallow clone of `this` by constructing a new instance
   * and then shallow cloning data members.
   */
  clone() {
    const constructor = this.constructor;
    const next = new constructor(this.configuration);
    next.middleware = this.middleware.slice();
    return next;
  }
};
function isBlob(value) {
  return typeof Blob !== "undefined" && value instanceof Blob;
}
function isFormData(value) {
  return typeof FormData !== "undefined" && value instanceof FormData;
}
function stringToDate(value) {
  return value ? new Date(value.replace(/Z$/, "")) : /* @__PURE__ */ new Date("invalid");
}
var ResponseError = class extends Error {
  constructor(response, msg, data) {
    super(msg);
    this.response = response;
    this.data = data;
  }
  name = "ResponseError";
  data;
};
var FetchError = class extends Error {
  constructor(cause, msg) {
    super(msg);
    this.cause = cause;
  }
  name = "FetchError";
};
var RequiredError = class extends Error {
  constructor(field, msg) {
    super(msg);
    this.field = field;
  }
  name = "RequiredError";
};
function querystring(params, prefix = "") {
  return Object.keys(params).map((key) => querystringSingleKey(key, params[key], prefix)).filter((part) => part.length > 0).join("&");
}
function querystringSingleKey(key, value, keyPrefix = "") {
  const fullKey = keyPrefix + (keyPrefix.length ? `[${key}]` : key);
  if (value instanceof Array) {
    const multiValue = value.map((singleValue) => encodeURIComponent(String(singleValue))).join(`&${encodeURIComponent(fullKey)}=`);
    return `${encodeURIComponent(fullKey)}=${multiValue}`;
  }
  if (value instanceof Set) {
    const valueAsArray = Array.from(value);
    return querystringSingleKey(key, valueAsArray, keyPrefix);
  }
  if (value instanceof Date) {
    return `${encodeURIComponent(fullKey)}=${encodeURIComponent(formatISO(value))}`;
  }
  if (value instanceof Object) {
    return querystring(value, fullKey);
  }
  return `${encodeURIComponent(fullKey)}=${encodeURIComponent(String(value))}`;
}
var JSONApiResponse = class {
  constructor(raw, transformer = (jsonValue) => jsonValue) {
    this.raw = raw;
    this.transformer = transformer;
  }
  async value() {
    return this.transformer(await this.raw.json());
  }
};

// src/rentals-api-v3/models/MandatoryProduct.ts
function MandatoryProductFromJSON(json) {
  return MandatoryProductFromJSONTyped(json, false);
}
function MandatoryProductFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    productId: json["productId"],
    sortOrder: json["sortOrder"],
    productCode: json["productCode"],
    name: json["name"],
    category: json["category"],
    isMandatory: json["isMandatory"],
    description: json["description"],
    price: CurrencyValueFromJSON(json["price"]),
    productType: json["productType"],
    isPercentageOnTotalCost: json["isPercentageOnTotalCost"],
    allowMultiple: json["allowMultiple"],
    commissionable: json["commissionable"],
    associatedProductCode: json["associatedProductCode"] == null ? void 0 : json["associatedProductCode"],
    rentalDays: json["rentalDays"],
    numberOfDaysCalculated: json["numberOfDaysCalculated"],
    maxPrice: json["maxPrice"] == null ? void 0 : CurrencyValueFromJSON(json["maxPrice"]),
    qty: json["qty"],
    total: CurrencyValueFromJSON(json["total"]),
    isExcluded: json["isExcluded"] == null ? void 0 : json["isExcluded"]
  };
}

// src/rentals-api-v3/models/FleetCategory.ts
function FleetCategoryFromJSON(json) {
  return FleetCategoryFromJSONTyped(json, false);
}
function FleetCategoryFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    name: json["name"],
    description: json["description"],
    code: json["code"],
    categoryCode: json["categoryCode"],
    campaignCode: json["campaignCode"] == null ? void 0 : json["campaignCode"],
    campaignId: json["campaignId"] == null ? void 0 : json["campaignId"],
    id: json["id"],
    type: json["type"],
    rentalDays: json["rentalDays"],
    dealText: json["dealText"] == null ? void 0 : json["dealText"],
    freeDays: json["freeDays"] == null ? void 0 : json["freeDays"],
    fleetTypeCode: json["fleetTypeCode"],
    availabilityMessage: json["availabilityMessage"],
    dailyRate: CurrencyValueFromJSON(json["dailyRate"]),
    total: CurrencyValueFromJSON(json["total"]),
    discountPercentageApplied: json["discountPercentageApplied"] == null ? void 0 : json["discountPercentageApplied"],
    mandatoryFees: json["mandatoryFees"] == null ? void 0 : json["mandatoryFees"].map(MandatoryProductFromJSON),
    minimumHirePeriod: json["minimumHirePeriod"] == null ? void 0 : json["minimumHirePeriod"],
    isAfterHourPickUp: json["isAfterHourPickUp"] == null ? void 0 : json["isAfterHourPickUp"],
    availability: json["availability"]
  };
}

// src/rentals-api-v3/models/AvailabilitySearchResponse.ts
function AvailabilitySearchResponseFromJSON(json) {
  return AvailabilitySearchResponseFromJSONTyped(json, false);
}
function AvailabilitySearchResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    pickUpLocation: json["pickUpLocation"],
    dropOffLocation: json["dropOffLocation"],
    pickUpDate: stringToDate(json["pickUpDate"]),
    dropOffDate: stringToDate(json["dropOffDate"]),
    rentalDays: json["rentalDays"],
    fleetCategories: json["fleetCategories"].map(FleetCategoryFromJSON)
  };
}

// src/rentals-api-v3/models/InsuranceType.ts
function InsuranceTypeFromJSON(json) {
  return InsuranceTypeFromJSONTyped(json, false);
}
function InsuranceTypeFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}

// src/rentals-api-v3/models/InsuranceProduct.ts
function InsuranceProductFromJSON(json) {
  return InsuranceProductFromJSONTyped(json, false);
}
function InsuranceProductFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    productId: json["productId"],
    sortOrder: json["sortOrder"],
    productCode: json["productCode"],
    name: json["name"],
    category: json["category"],
    isMandatory: json["isMandatory"],
    description: json["description"],
    price: CurrencyValueFromJSON(json["price"]),
    productType: json["productType"],
    isPercentageOnTotalCost: json["isPercentageOnTotalCost"],
    allowMultiple: json["allowMultiple"],
    commissionable: json["commissionable"],
    associatedProductCode: json["associatedProductCode"] == null ? void 0 : json["associatedProductCode"],
    rentalDays: json["rentalDays"],
    numberOfDaysCalculated: json["numberOfDaysCalculated"],
    maxPrice: json["maxPrice"] == null ? void 0 : CurrencyValueFromJSON(json["maxPrice"]),
    excessType: InsuranceTypeFromJSON(json["excessType"]),
    excess: CurrencyValueFromJSON(json["excess"]),
    bond: CurrencyValueFromJSON(json["bond"]),
    rcmId: json["rcmId"] == null ? void 0 : json["rcmId"]
  };
}

// src/rentals-api-v3/models/InsuranceProductGroup.ts
function InsuranceProductGroupFromJSON(json) {
  return InsuranceProductGroupFromJSONTyped(json, false);
}
function InsuranceProductGroupFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    group: json["group"],
    webCategory: json["webCategory"],
    required: json["required"],
    allowMultiple: json["allowMultiple"],
    description: json["description"],
    items: json["items"].map(InsuranceProductFromJSON)
  };
}

// src/rentals-api-v3/models/SecondaryProduct.ts
function SecondaryProductFromJSON(json) {
  return SecondaryProductFromJSONTyped(json, false);
}
function SecondaryProductFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    productId: json["productId"],
    sortOrder: json["sortOrder"],
    productCode: json["productCode"],
    name: json["name"],
    category: json["category"],
    isMandatory: json["isMandatory"],
    description: json["description"],
    price: CurrencyValueFromJSON(json["price"]),
    productType: json["productType"],
    isPercentageOnTotalCost: json["isPercentageOnTotalCost"],
    allowMultiple: json["allowMultiple"],
    commissionable: json["commissionable"],
    associatedProductCode: json["associatedProductCode"] == null ? void 0 : json["associatedProductCode"],
    rentalDays: json["rentalDays"],
    numberOfDaysCalculated: json["numberOfDaysCalculated"],
    maxPrice: json["maxPrice"] == null ? void 0 : CurrencyValueFromJSON(json["maxPrice"])
  };
}

// src/rentals-api-v3/models/ProductBundle.ts
function ProductBundleFromJSON(json) {
  return ProductBundleFromJSONTyped(json, false);
}
function ProductBundleFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    productCode: json["productCode"],
    bundledProducts: json["bundledProducts"].map(SecondaryProductFromJSON)
  };
}

// src/rentals-api-v3/models/SecondaryProductGroup.ts
function SecondaryProductGroupFromJSON(json) {
  return SecondaryProductGroupFromJSONTyped(json, false);
}
function SecondaryProductGroupFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    group: json["group"],
    webCategory: json["webCategory"],
    required: json["required"],
    allowMultiple: json["allowMultiple"],
    description: json["description"],
    items: json["items"].map(SecondaryProductFromJSON)
  };
}

// src/rentals-api-v3/models/ProductLine.ts
function ProductLineFromJSON(json) {
  return ProductLineFromJSONTyped(json, false);
}
function ProductLineFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    ...json,
    productId: json["productId"],
    sortOrder: json["sortOrder"],
    productCode: json["productCode"],
    name: json["name"],
    category: json["category"],
    isMandatory: json["isMandatory"],
    description: json["description"],
    price: CurrencyValueFromJSON(json["price"]),
    productType: json["productType"],
    isPercentageOnTotalCost: json["isPercentageOnTotalCost"],
    allowMultiple: json["allowMultiple"],
    commissionable: json["commissionable"],
    associatedProductCode: json["associatedProductCode"] == null ? void 0 : json["associatedProductCode"],
    rentalDays: json["rentalDays"],
    numberOfDaysCalculated: json["numberOfDaysCalculated"],
    maxPrice: json["maxPrice"] == null ? void 0 : CurrencyValueFromJSON(json["maxPrice"]),
    qty: json["qty"],
    total: CurrencyValueFromJSON(json["total"]),
    isExcluded: json["isExcluded"] == null ? void 0 : json["isExcluded"]
  };
}

// src/rentals-api-v3/models/TripDetails.ts
function TripDetailsFromJSON(json) {
  return TripDetailsFromJSONTyped(json, false);
}
function TripDetailsFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    pickUpLocation: json["pickUpLocation"],
    dropOffLocation: json["dropOffLocation"],
    pickUpDate: stringToDate(json["pickUpDate"]),
    dropOffDate: stringToDate(json["dropOffDate"]),
    rentalDays: json["rentalDays"],
    totalPrice: CurrencyValueFromJSON(json["totalPrice"]),
    lines: json["lines"] == null ? void 0 : json["lines"].map(ProductLineFromJSON),
    fleetCategory: FleetCategoryFromJSON(json["fleetCategory"]),
    secondaryProducts: json["secondaryProducts"].map(SecondaryProductGroupFromJSON),
    insuranceProducts: InsuranceProductGroupFromJSON(json["insuranceProducts"]),
    bundles: json["bundles"] == null ? void 0 : json["bundles"].map(ProductBundleFromJSON)
  };
}

// src/rentals-api-v3/apis/TripApi.ts
var TripApi = class extends BaseAPI {
  /**
   * Search for availability and pricing for alternative dates
   */
  async alternateDatesSearchRaw(requestParameters, initOverrides) {
    if (requestParameters["pickUpLocation"] == null) {
      throw new RequiredError(
        "pickUpLocation",
        'Required parameter "pickUpLocation" was null or undefined when calling alternateDatesSearch().'
      );
    }
    if (requestParameters["dropOffLocation"] == null) {
      throw new RequiredError(
        "dropOffLocation",
        'Required parameter "dropOffLocation" was null or undefined when calling alternateDatesSearch().'
      );
    }
    if (requestParameters["pickUpDate"] == null) {
      throw new RequiredError(
        "pickUpDate",
        'Required parameter "pickUpDate" was null or undefined when calling alternateDatesSearch().'
      );
    }
    if (requestParameters["dropOffDate"] == null) {
      throw new RequiredError(
        "dropOffDate",
        'Required parameter "dropOffDate" was null or undefined when calling alternateDatesSearch().'
      );
    }
    if (requestParameters["fleetTypeCode"] == null) {
      throw new RequiredError(
        "fleetTypeCode",
        'Required parameter "fleetTypeCode" was null or undefined when calling alternateDatesSearch().'
      );
    }
    if (requestParameters["days"] == null) {
      throw new RequiredError("days", 'Required parameter "days" was null or undefined when calling alternateDatesSearch().');
    }
    const queryParameters = {};
    if (requestParameters["pickUpLocation"] != null) {
      queryParameters["pickUpLocation"] = requestParameters["pickUpLocation"];
    }
    if (requestParameters["dropOffLocation"] != null) {
      queryParameters["dropOffLocation"] = requestParameters["dropOffLocation"];
    }
    if (requestParameters["pickUpDate"] != null) {
      queryParameters["pickUpDate"] = requestParameters["pickUpDate"];
    }
    if (requestParameters["dropOffDate"] != null) {
      queryParameters["dropOffDate"] = requestParameters["dropOffDate"];
    }
    if (requestParameters["fleetTypeCode"] != null) {
      queryParameters["fleetTypeCode"] = requestParameters["fleetTypeCode"];
    }
    if (requestParameters["campaignCode"] != null) {
      queryParameters["campaignCode"] = requestParameters["campaignCode"];
    }
    if (requestParameters["days"] != null) {
      queryParameters["days"] = requestParameters["days"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request(
      {
        path: `/api/v3/trip/alternate-dates-search`,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => jsonValue.map(AvailabilitySearchResponseFromJSON));
  }
  /**
   * Search for availability and pricing for alternative dates
   */
  async alternateDatesSearch(pickUpLocation, dropOffLocation, pickUpDate, dropOffDate, fleetTypeCode, days, campaignCode, initOverrides) {
    const response = await this.alternateDatesSearchRaw(
      {
        pickUpLocation,
        dropOffLocation,
        pickUpDate,
        dropOffDate,
        fleetTypeCode,
        days,
        campaignCode
      },
      initOverrides
    );
    return await response.value();
  }
  /**
   * Search for availability and pricing
   */
  async availabilitySearchRaw(requestParameters, initOverrides) {
    if (requestParameters["pickUpLocation"] == null) {
      throw new RequiredError(
        "pickUpLocation",
        'Required parameter "pickUpLocation" was null or undefined when calling availabilitySearch().'
      );
    }
    if (requestParameters["dropOffLocation"] == null) {
      throw new RequiredError(
        "dropOffLocation",
        'Required parameter "dropOffLocation" was null or undefined when calling availabilitySearch().'
      );
    }
    if (requestParameters["pickUpDate"] == null) {
      throw new RequiredError("pickUpDate", 'Required parameter "pickUpDate" was null or undefined when calling availabilitySearch().');
    }
    if (requestParameters["dropOffDate"] == null) {
      throw new RequiredError(
        "dropOffDate",
        'Required parameter "dropOffDate" was null or undefined when calling availabilitySearch().'
      );
    }
    if (requestParameters["fleetTypeCode"] == null) {
      throw new RequiredError(
        "fleetTypeCode",
        'Required parameter "fleetTypeCode" was null or undefined when calling availabilitySearch().'
      );
    }
    const queryParameters = {};
    if (requestParameters["pickUpLocation"] != null) {
      queryParameters["pickUpLocation"] = requestParameters["pickUpLocation"];
    }
    if (requestParameters["dropOffLocation"] != null) {
      queryParameters["dropOffLocation"] = requestParameters["dropOffLocation"];
    }
    if (requestParameters["pickUpDate"] != null) {
      queryParameters["pickUpDate"] = requestParameters["pickUpDate"];
    }
    if (requestParameters["dropOffDate"] != null) {
      queryParameters["dropOffDate"] = requestParameters["dropOffDate"];
    }
    if (requestParameters["fleetTypeCode"] != null) {
      queryParameters["fleetTypeCode"] = requestParameters["fleetTypeCode"];
    }
    if (requestParameters["driverAge"] != null) {
      queryParameters["driverAge"] = requestParameters["driverAge"];
    }
    if (requestParameters["campaignCode"] != null) {
      queryParameters["campaignCode"] = requestParameters["campaignCode"];
    }
    if (requestParameters["mergeSurchargesFees"] != null) {
      queryParameters["mergeSurchargesFees"] = requestParameters["mergeSurchargesFees"];
    }
    if (requestParameters["reservationReference"] != null) {
      queryParameters["reservationReference"] = requestParameters["reservationReference"];
    }
    if (requestParameters["pascal"] != null) {
      queryParameters["pascal"] = requestParameters["pascal"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request(
      {
        path: `/api/v3/trip/availability`,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => AvailabilitySearchResponseFromJSON(jsonValue));
  }
  /**
   * Search for availability and pricing
   */
  async availabilitySearch(pickUpLocation, dropOffLocation, pickUpDate, dropOffDate, fleetTypeCode, driverAge, campaignCode, mergeSurchargesFees, reservationReference, pascal, initOverrides) {
    const response = await this.availabilitySearchRaw(
      {
        pickUpLocation,
        dropOffLocation,
        pickUpDate,
        dropOffDate,
        fleetTypeCode,
        driverAge,
        campaignCode,
        mergeSurchargesFees,
        reservationReference,
        pascal
      },
      initOverrides
    );
    return await response.value();
  }
  /**
   * Get pricing, extras and insurance options for a trip
   */
  async tripInfoRaw(requestParameters, initOverrides) {
    if (requestParameters["pickUpLocation"] == null) {
      throw new RequiredError("pickUpLocation", 'Required parameter "pickUpLocation" was null or undefined when calling tripInfo().');
    }
    if (requestParameters["dropOffLocation"] == null) {
      throw new RequiredError("dropOffLocation", 'Required parameter "dropOffLocation" was null or undefined when calling tripInfo().');
    }
    if (requestParameters["pickUpDate"] == null) {
      throw new RequiredError("pickUpDate", 'Required parameter "pickUpDate" was null or undefined when calling tripInfo().');
    }
    if (requestParameters["dropOffDate"] == null) {
      throw new RequiredError("dropOffDate", 'Required parameter "dropOffDate" was null or undefined when calling tripInfo().');
    }
    if (requestParameters["fleetCategory"] == null) {
      throw new RequiredError("fleetCategory", 'Required parameter "fleetCategory" was null or undefined when calling tripInfo().');
    }
    const queryParameters = {};
    if (requestParameters["pickUpLocation"] != null) {
      queryParameters["pickUpLocation"] = requestParameters["pickUpLocation"];
    }
    if (requestParameters["dropOffLocation"] != null) {
      queryParameters["dropOffLocation"] = requestParameters["dropOffLocation"];
    }
    if (requestParameters["pickUpDate"] != null) {
      queryParameters["pickUpDate"] = requestParameters["pickUpDate"];
    }
    if (requestParameters["dropOffDate"] != null) {
      queryParameters["dropOffDate"] = requestParameters["dropOffDate"];
    }
    if (requestParameters["fleetCategory"] != null) {
      queryParameters["fleetCategory"] = requestParameters["fleetCategory"];
    }
    if (requestParameters["driverAge"] != null) {
      queryParameters["driverAge"] = requestParameters["driverAge"];
    }
    if (requestParameters["numberOfPeople"] != null) {
      queryParameters["numberOfPeople"] = requestParameters["numberOfPeople"];
    }
    if (requestParameters["campaignCode"] != null) {
      queryParameters["campaignCode"] = requestParameters["campaignCode"];
    }
    if (requestParameters["pascal"] != null) {
      queryParameters["pascal"] = requestParameters["pascal"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request(
      {
        path: `/api/v3/trip/info`,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => TripDetailsFromJSON(jsonValue));
  }
  /**
   * Get pricing, extras and insurance options for a trip
   */
  async tripInfo(pickUpLocation, dropOffLocation, pickUpDate, dropOffDate, fleetCategory, driverAge, numberOfPeople, campaignCode, pascal, initOverrides) {
    const response = await this.tripInfoRaw(
      {
        pickUpLocation,
        dropOffLocation,
        pickUpDate,
        dropOffDate,
        fleetCategory,
        driverAge,
        numberOfPeople,
        campaignCode,
        pascal
      },
      initOverrides
    );
    return await response.value();
  }
};
export {
  TripApi
};
//# sourceMappingURL=TripApi.mjs.map